import React, { useState } from 'react';
import Sheet from '../../shared/components/Sheet';
import { useEditProtection } from '../../shared/components/EditProtection';
import { useAuth } from '../../contexts/AuthContext';
import { apiSend } from './api';
import { fmtCurrency, fmtNumber } from './formatters';
import type { SalaryEntry } from './types';

const RateHoursEditor: React.FC<{ entry: SalaryEntry; onClose: () => void; onSaved: () => void }> = ({ entry, onClose, onSaved }) => {
  const { sessionToken } = useAuth();
  const segments = entry.rate_segments || [];
  const [values, setValues] = useState(() => segments.map(segment => {
    const saved = entry.rate_hours?.find(row => row.start_date === segment.start_date);
    return saved ? String(saved.hours) : '';
  }));
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  useEditProtection(dirty);
  const validHours = values.every(v => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0 && /^\d+(\.\d{1,2})?$/.test(v));
  const assigned = values.reduce((sum, v) => sum + Math.round(Number(v || 0) * 100), 0);
  const balanced = validHours && assigned === Math.round(Number(entry.hours) * 100);
  const requestClose = () => { if (!busy && (!dirty || window.confirm('Discard the hours split?'))) onClose(); };
  const save = async () => {
    if (!balanced) return;
    setBusy(true); setError('');
    try {
      await apiSend(`/periods/${entry.period_id}/entries`, 'POST', {
        employee_id: entry.employee_id, hours: Number(entry.hours),
        rate_hours: segments.map((segment, i) => ({ start_date: segment.start_date, hours: Number(values[i]) })),
      }, sessionToken);
      setDirty(false); onSaved(); onClose();
    } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  };
  return <Sheet title={`Split hours — ${entry.employee_name}`} onClose={requestClose}>
    <p>Allocate the actual hours worked in each date range. Total period hours: <strong>{fmtNumber(entry.hours)}</strong>.</p>
    {error && <div role="alert" className="error-banner">{error}</div>}
    {segments.map((segment, i) => <label className="salary-rate-allocation" key={segment.start_date}>
      <span>{segment.start_date} → {segment.end_date} · {fmtCurrency(segment.hourly_rate)}/hr</span>
      <input type="number" min="0" max="336" step="0.01" inputMode="decimal"
        aria-label={`Hours ${segment.start_date} to ${segment.end_date}`} value={values[i]} disabled={busy}
        onChange={event => { setDirty(true); setValues(previous => previous.map((v, index) => index === i ? event.target.value : v)); }} />
    </label>)}
    <p role="status">Assigned: {fmtNumber(assigned / 100)} / {fmtNumber(entry.hours)} hours. Enter zero for any range with no work.</p>
    <div className="salary-period-row-actions">
      <button className="salary-btn" disabled={!balanced || busy} onClick={save}>{busy ? 'Saving…' : 'Save split and calculate'}</button>
      <button className="salary-btn secondary" disabled={busy} onClick={requestClose}>Cancel</button>
    </div>
  </Sheet>;
};

export default RateHoursEditor;
